import type {ReactElement} from 'react';
import React from 'react';
import styles from './notifications-settings.module.scss';
import {Text, TextSize} from '@Components/text';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2';
import {useAppDispatch} from '@/hooks';
import {useNotificationSettingsPanelData} from '@Panels/notifications-panel/notifications-panel-hooks';
import {showDesignUpdates, showEventsUpdates, showEmailCampaignUpdates, showSocialMediaPostsUpdates, showTeamUpdates} from '@Panels/notifications-panel/notifications-panel-slice';
import type {NotificationsSettingsProps} from '@Panels/notifications-panel/components/notifications-settings/notifications-settings.types';
import {GA4EventName, GA4EventParamName, NotificationsSettingsToggleSource} from '@Libraries/ga-events';

export function NotificationsSettings(props: NotificationsSettingsProps): ReactElement {
  const dispatch = useAppDispatch();
  const settings = useNotificationSettingsPanelData();

  const getSettingsToggleItem = (label: string, isChecked: boolean, onToggle: () => void): ReactElement => (
    <div className="flex-row-justify-between">
      <Text val={label} size={TextSize.XSMALL} className="flex-center" />
      <ToggleSwitch isChecked={isChecked} onChange={onToggle} size={ToggleSwitchV2Size.SMALL} />
    </div>
  );

  const setDesignUpdates = (): void => {
    dispatch(showDesignUpdates(!settings.designUpdates));
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.NOTIFICATION_TOGGLE, {[GA4EventParamName.VALUE]: NotificationsSettingsToggleSource.DESIGN});
  };

  const setTeamUpdates = (): void => {
    dispatch(showTeamUpdates(!settings.teamUpdates));
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.NOTIFICATION_TOGGLE, {[GA4EventParamName.VALUE]: NotificationsSettingsToggleSource.TEAM});
  };
  const setEmailCampaignUpdates = (): void => {
    dispatch(showEmailCampaignUpdates(!settings.emailCampaigns));
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.NOTIFICATION_TOGGLE, {[GA4EventParamName.VALUE]: NotificationsSettingsToggleSource.EMAILS});
  };

  const setSMPublishingUpdates = (): void => {
    dispatch(showSocialMediaPostsUpdates(!settings.socialMediaPosts));
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.NOTIFICATION_TOGGLE, {[GA4EventParamName.VALUE]: NotificationsSettingsToggleSource.SM_POSTS});
  };

  const setEventsUpdates = (): void => {
    dispatch(showEventsUpdates(!settings.eventsUpdates));
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.NOTIFICATION_TOGGLE, {[GA4EventParamName.VALUE]: NotificationsSettingsToggleSource.EVENTS});
  };

  return (
    <div className={`${props.className} ${styles.settingsPanelGap} flex-v-row spacing-p-4`}>
      {getSettingsToggleItem(window.i18next.t('pmwjs_updates_on_design'), settings.designUpdates, () => {
        setDesignUpdates();
      })}
      {getSettingsToggleItem(window.i18next.t('pmwjs_updates_on_team'), settings.teamUpdates, () => {
        setTeamUpdates();
      })}
      {getSettingsToggleItem(window.i18next.t('pmwjs_updates_on_email_campaigns'), settings.emailCampaigns, () => {
        setEmailCampaignUpdates();
      })}
      {getSettingsToggleItem(window.i18next.t('pmwjs_updates_on_sm_posts'), settings.socialMediaPosts, () => {
        setSMPublishingUpdates();
      })}
      {getSettingsToggleItem(window.i18next.t('pmwjs_updates_on_events'), settings.eventsUpdates, () => {
        setEventsUpdates();
      })}
    </div>
  );
}
